import React, { useEffect } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import InfluencerIdentityRow, {
  InfluencerIdentityRowRoot,
} from '@components/InfluencerIdentity/Row'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { INBOX_FILTER } from '@constants'
import { InfluencerIdentityIdProvider } from '@context/InfluencerIdentityId'
import { EventTrackingNames } from '@enums'
import { IApplicationState } from '@store'
import {
  selectInfluencerIdentity,
  selectInfluencerIdentityFormattedLocationAndGender,
  selectInfluencerIdentityHasGroup,
} from '@store/influencerIdentities'
import {
  fetchSubmissionsByInfluencerIdentity,
  selectSubmissionIdsByInfluencerIdentity,
} from '@store/submissions'
import { isEmpty } from '@utils'
import InfluencerIdentityRowSubmissionWrapper from './Submission/Wrapper/Wrapper'
import InboxInfluencerIdentityRowLabels from './Labels'
import InfluencerIdentityRowPerformance from './Performance'

interface IProps {
  influencerIdentityId: number
  campaignId: number
}

export const InboxInfluencerIdentityRow: React.FC<IProps> = ({
  campaignId,
  influencerIdentityId,
}) => {
  const dispatch = useDispatch()

  const hasFetched = useSelector(
    (state: IApplicationState) =>
      !isEmpty(selectSubmissionIdsByInfluencerIdentity(state, campaignId, influencerIdentityId)),
  )
  const { age, avatarSrc, socialUrl, fullName, username, provider } = useSelector(
    (state: IApplicationState) => {
      const identity = selectInfluencerIdentity(state, campaignId, influencerIdentityId)
      return {
        age: identity.influencer_age,
        avatarSrc: identity.identity_avatar,
        socialUrl: identity.identity_social_page_url,
        fullName: identity.influencer_full_name,
        username: identity.identity_username,
        provider: identity.identity_provider,
      }
    },
    shallowEqual,
  )
  const locationAndGender = useSelector((state: IApplicationState) =>
    selectInfluencerIdentityFormattedLocationAndGender(state, campaignId, influencerIdentityId),
  )
  const hasGroup = useSelector((state: IApplicationState) =>
    selectInfluencerIdentityHasGroup(state, campaignId, influencerIdentityId),
  )

  const [isOpen, setIsOpen] = React.useState(false)
  const onFetch = () => {
    if (isOpen && !hasFetched) {
      dispatch(fetchSubmissionsByInfluencerIdentity(campaignId, influencerIdentityId))
    }
  }
  useEffect(onFetch, [isOpen])

  const onRowClick = () => setIsOpen(!isOpen)

  return (
    <InfluencerIdentityIdProvider influencerIdentityId={influencerIdentityId}>
      <InfluencerIdentityRowRoot>
        <ConnectedTrackedRoute
          disableTracking={isOpen}
          eventName={EventTrackingNames.ListViewRowClicked}
          campaignId={campaignId}
          additionalProperties={{ identity_id: influencerIdentityId, current_filter: INBOX_FILTER }}
        >
          <InfluencerIdentityRow
            onRowClick={onRowClick}
            isOpen={isOpen}
            stats={<InfluencerIdentityRowPerformance />}
            socialUrl={socialUrl}
            locationAndGender={locationAndGender}
            provider={provider}
            avatarSrc={avatarSrc}
            age={age}
            fullName={fullName}
            labels={<InboxInfluencerIdentityRowLabels />}
            username={username}
            showStar={hasGroup}
          />
        </ConnectedTrackedRoute>
      </InfluencerIdentityRowRoot>
      {isOpen && <InfluencerIdentityRowSubmissionWrapper />}
    </InfluencerIdentityIdProvider>
  )
}

export default React.memo(InboxInfluencerIdentityRow)
